import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import type { CSSProperties } from 'react';
import { ComponentSize } from '../../types';
import type { ButtonVariant, IconButtonSize, IconButtonVariant } from './types';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const sizeMapper: Record<ComponentSize, 'small' | 'medium' | 'large'> = {
  [ComponentSize.Small]: 'small',
  [ComponentSize.Medium]: 'medium',
  [ComponentSize.Large]: 'large',
  [ComponentSize.XSmall]: 'small',
  [ComponentSize.XXSmall]: 'small',
};

const focusVisibleStyles = (theme: any) => `
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid ${theme?.colors?.text?.primary ?? '#292929'};
    outline-offset: 2px;
  }
`;

type StyledButtonProps = {
  $variant: ButtonVariant;
  $size: ComponentSize;
  $fullWidth?: boolean;
  $underline?: boolean;
  $loading?: boolean;
  $active?: boolean;
  alignItems?: CSSProperties['alignItems'];
  /** Present when rendered as an anchor via Emotion `as` */
  href?: string;
};

function getVariantStyles(theme: any, variant: ButtonVariant, active?: boolean) {
  const tokens = theme?.component?.button?.[variant];
  if (!tokens) {
    return '';
  }

  const activeTokens = tokens.active ?? tokens;
  const hover = tokens.hover ?? {
    backgroundColor: tokens.hoverBackgroundColor,
    color: tokens.color,
    borderColor: tokens.hoverBorderColor,
  };

  if (variant === 'chip') {
    const selected = Boolean(active);
    return `
      background-color: ${
        selected
          ? activeTokens.backgroundColor ?? '#292929'
          : tokens.backgroundColor ?? '#ffffff'
      };
      color: ${selected ? activeTokens.color ?? '#ffffff' : tokens.color ?? '#292929'};
      border: 1px solid ${
        selected
          ? activeTokens.borderColor ?? '#292929'
          : tokens.borderColor ?? theme?.colors?.border?.color ?? '#e5e5e5'
      };
      min-width: auto;
      min-height: auto;
      border-radius: ${theme?.radius?.small ?? '4px'};
      padding: 0.5rem 0.75rem;
      font-size: 0.875rem;
      text-transform: none;

      @media (hover: hover) and (pointer: fine) {
        &:hover:not(:disabled) {
          background-color: ${
            selected
              ? activeTokens.backgroundColor ?? '#292929'
              : hover.backgroundColor ?? '#f5f5f5'
          };
          border-color: ${
            selected
              ? activeTokens.borderColor ?? '#292929'
              : hover.borderColor ?? '#292929'
          };
        }
      }
    `;
  }

  if (variant === 'tab') {
    const selected = Boolean(active);
    return `
      background-color: transparent;
      color: ${selected ? activeTokens.color ?? '#292929' : tokens.color ?? '#808080'};
      border: none;
      border-bottom: 2px solid ${
        selected
          ? activeTokens.borderColor ?? theme?.colors?.primary ?? '#f7a08b'
          : 'transparent'
      };
      border-radius: 0;
      min-width: auto;
      min-height: auto;
      padding: 0.75rem 1rem;
      font-weight: ${selected ? 600 : 400};

      @media (hover: hover) and (pointer: fine) {
        &:hover:not(:disabled) {
          color: ${hover.color ?? '#292929'};
        }
      }
    `;
  }

  if (variant === 'link') {
    return `
      background-color: transparent;
      color: ${tokens.color ?? theme?.colors?.text?.primary ?? '#292929'};
      border: none;
      min-width: auto;
      min-height: auto;
      padding: 0;
      font-weight: 400;
      text-decoration: underline;

      @media (hover: hover) and (pointer: fine) {
        &:hover:not(:disabled) {
          color: ${hover.color ?? '#5a5a5a'};
        }
      }
    `;
  }

  const border =
    variant === 'outline'
      ? `1px solid ${activeTokens.borderColor ?? tokens.borderColor ?? theme?.colors?.border?.color}`
      : theme?.component?.button?.border ?? 'none';

  return `
    background-color: ${activeTokens.backgroundColor ?? tokens.backgroundColor};
    color: ${activeTokens.color ?? tokens.color};
    border: ${border};

    @media (hover: hover) and (pointer: fine) {
      &:hover:not(:disabled):not([aria-disabled='true']) {
        background-color: ${hover.backgroundColor ?? tokens.hoverBackgroundColor};
        color: ${hover.color ?? tokens.color};
        ${
          variant === 'outline'
            ? `border-color: ${hover.borderColor ?? tokens.hoverBorderColor};`
            : ''
        }
      }
    }
  `;
}

export const StyledButton = styled.button<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-sizing: border-box;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'fit-content')};
  font-family: inherit;
  font-weight: ${({ theme }) => theme?.component?.button?.fontWeight};
  line-height: ${({ theme }) => theme?.component?.button?.lineHeight};
  letter-spacing: ${({ theme }) => theme?.component?.button?.letterSpacing};
  border-radius: ${({ theme }) => theme?.component?.button?.borderRadius};
  cursor: ${({ $loading }) => ($loading ? 'wait' : 'pointer')};
  transition: all 0.2s ease;
  text-decoration: ${({ $underline }) => ($underline ? 'underline' : 'none')};
  min-width: ${({ theme, $variant }) =>
    $variant === 'ghost' ||
    $variant === 'destructive' ||
    $variant === 'chip' ||
    $variant === 'tab' ||
    $variant === 'link'
      ? 'auto'
      : theme?.component?.button?.minWidth};
  min-height: ${({ theme, $variant }) =>
    $variant === 'ghost' ||
    $variant === 'destructive' ||
    $variant === 'chip' ||
    $variant === 'tab' ||
    $variant === 'link'
      ? 'auto'
      : theme?.component?.button?.minHeight};
  font-size: ${({ theme, $size }) =>
    theme?.component?.button?.size?.[sizeMapper[$size]]?.fontSize};
  padding: ${({ theme, $size, $variant }) =>
    $variant === 'ghost' || $variant === 'link'
      ? '0'
      : theme?.component?.button?.size?.[sizeMapper[$size]]?.padding};
  ${({ alignItems }) => (alignItems ? `align-items: ${alignItems};` : '')}
  ${({ theme, $variant, $active }) => getVariantStyles(theme, $variant, $active)}
  ${({ theme }) => focusVisibleStyles(theme)}

  &:disabled,
  &[aria-disabled='true'] {
    background-color: ${({ theme, $variant }) =>
      $variant === 'ghost' ||
      $variant === 'destructive' ||
      $variant === 'link' ||
      $variant === 'tab'
        ? 'transparent'
        : theme?.colors?.background?.disabled};
    color: ${({ theme }) => theme?.colors?.text?.disabled};
    border-color: transparent;
    cursor: not-allowed;
    opacity: ${({ $variant }) =>
      $variant === 'ghost' ||
      $variant === 'destructive' ||
      $variant === 'link' ||
      $variant === 'tab'
        ? 0.5
        : 1};
    pointer-events: none;
  }

  svg {
    color: inherit;
    flex-shrink: 0;
  }
`;

type StyledIconButtonProps = {
  $variant: IconButtonVariant;
  $size: IconButtonSize;
  /** Present when rendered as an anchor via Emotion `as` */
  href?: string;
};

export const StyledIconButton = styled.button<StyledIconButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: none;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: ${({ theme }) => theme?.component?.button?.borderRadius};
  /* WCAG 2.5.8 AA: minimum 44×44px hit target for all sizes */
  min-width: 44px;
  min-height: 44px;
  padding: ${({ theme, $size }) =>
    theme?.component?.button?.icon?.size?.[$size]?.padding ?? '0.5rem'};
  font-size: ${({ theme, $size }) =>
    theme?.component?.button?.icon?.size?.[$size]?.fontSize ?? '1rem'};
  background-color: ${({ theme, $variant }) =>
    theme?.component?.button?.[$variant]?.active?.backgroundColor ??
    theme?.component?.button?.[$variant]?.backgroundColor ??
    'transparent'};
  color: ${({ theme, $variant }) =>
    theme?.component?.button?.[$variant]?.active?.color ??
    theme?.component?.button?.[$variant]?.color ??
    'inherit'};
  border: ${({ theme, $variant }) =>
    $variant === 'outline'
      ? `1px solid ${
          theme?.component?.button?.outline?.active?.borderColor ??
          theme?.colors?.border?.color
        }`
      : 'none'};
  ${({ theme }) => focusVisibleStyles(theme)}

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled):not([aria-disabled='true']) {
      background-color: ${({ theme, $variant }) =>
        theme?.component?.button?.[$variant]?.hover?.backgroundColor ??
        theme?.component?.button?.[$variant]?.hoverBackgroundColor ??
        'transparent'};
      color: ${({ theme, $variant }) =>
        theme?.component?.button?.[$variant]?.hover?.color ??
        theme?.component?.button?.[$variant]?.color};
      opacity: ${({ $variant }) => ($variant === 'ghost' ? 0.7 : 1)};
    }
  }

  &:disabled,
  &[aria-disabled='true'] {
    color: ${({ theme }) => theme?.colors?.text?.disabled};
    cursor: not-allowed;
    opacity: 0.5;
    pointer-events: none;
  }

  svg {
    color: inherit;
    width: 1em;
    height: 1em;
  }
`;

export const ButtonContent = styled.span<{ $hidden?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
`;

export const Spinner = styled.span`
  position: absolute;
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    border-right-color: currentColor;
    opacity: 0.6;
  }
`;

export const ButtonInner = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
`;

/** Visually hidden text for assistive technologies */
export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
